import { useCallback, useEffect, useRef, useState } from 'react'

import { describeFailure, mountPlayer } from '@/player/standalone'
import type { FriendlyError, MountedPlayer } from '@/player/standalone'

/**
 * The story player, mounted from an IN-MEMORY bundle (an action preview is never saved as a
 * story). `components/StoryPlayer.tsx` only loads by story uid / bundle url, so this is its twin
 * for a bundle object: the same `player/standalone#mountPlayer` in `preview` mode, the same host
 * contract (the player owns everything it appends; we only clear the host on unmount).
 *
 * `bundleUrl` is where the bundle's RELATIVE asset paths resolve from (its directory is the base).
 */

export interface BundlePlayerProps {
  bundle: unknown
  bundleUrl: string
  className?: string
  onEnded?: () => void
}

export function BundlePlayer({ bundle, bundleUrl, className, onEnded }: BundlePlayerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const [failure, setFailure] = useState<FriendlyError | null>(null)
  const [starting, setStarting] = useState(true)
  const [attempt, setAttempt] = useState(0)
  const ended = useRef(onEnded)
  ended.current = onEnded

  useEffect(() => {
    const host = hostRef.current
    if (!host || !bundle) return
    let disposed = false
    let mounted: MountedPlayer | null = null
    const href = new URL(bundleUrl, window.location.href).href
    setFailure(null)
    setStarting(true)
    void (async () => {
      try {
        const m = await mountPlayer(host, bundle, { bundleUrl: href, ui: 'preview' })
        if (disposed) {
          m.destroy()
          return
        }
        mounted = m
        m.player.on('ended', () => ended.current?.())
        m.player.on('error', (err: unknown) => m.hud.showError(err instanceof Error ? err.message : 'The preview stopped.'))
        setStarting(false)
      } catch (err) {
        if (disposed) return
        setStarting(false)
        setFailure(describeFailure(err, href))
      }
    })()
    return () => {
      disposed = true
      mounted?.destroy()
      host.textContent = ''
    }
  }, [bundle, bundleUrl, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  return (
    <div className={['relative aspect-video w-full overflow-hidden rounded-xl bg-black text-[#f1ebe1]', className].filter(Boolean).join(' ')} data-testid="bundle-player" data-starting={starting || undefined}>
      <div ref={hostRef} data-story-player-host="" className="absolute inset-0" style={{ touchAction: 'manipulation', userSelect: 'none' }} />
      {failure ? (
        <div className="absolute inset-0 grid place-items-center bg-[#14120f] p-4 text-center" role="alert">
          <div className="flex max-w-md flex-col items-center gap-3">
            <p className="m-0 text-lg font-semibold">{failure.title}</p>
            <p className="m-0 text-sm text-[#a79c90]">{failure.detail}</p>
            <button type="button" onClick={retry} className="min-h-11 rounded-full bg-[#e8734a] px-5 text-sm font-semibold text-white">
              Try again
            </button>
            {failure.technical ? (
              <details className="max-w-full text-xs text-[#7a7067]">
                <summary className="cursor-pointer">What went wrong?</summary>
                <pre className="m-0 mt-2 whitespace-pre-wrap break-words text-left">{failure.technical}</pre>
              </details>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}
